import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	Button,
	TextField,
	FormControlLabel,
	Switch,
	Typography,
	Grid,
} from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	IAIModel,
	ICreateAIModelRequest,
} from 'src/features/ai_model/domain/types';
import { CreateUpdateAIModelValidationSchema } from 'src/features/ai_model/domain/validation';
import {
	AI_MODEL_LOADING_KEY,
	updateAIModel,
} from 'src/features/ai_model/store';
import { AppDispatch, RootState } from 'src/store';

interface IAIModelDetailFormProps {
	aiModel: IAIModel;
}

const AIModelDetailForm: React.FC<IAIModelDetailFormProps> = ({ aiModel }) => {
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(
		(state: RootState) => state.loading.loadingStates[AI_MODEL_LOADING_KEY],
	);

	const {
		control,
		reset,
		handleSubmit,
		formState: { errors, isDirty },
	} = useForm<ICreateAIModelRequest>({
		resolver: yupResolver(CreateUpdateAIModelValidationSchema),
		defaultValues: {
			name: aiModel.name,
			description: aiModel.description || '',
			active: aiModel.active,
			inference_endpoint: aiModel.inference_endpoint,
			liveness_endpoint: aiModel.liveness_endpoint || '',
			batch_inference_endpoint: aiModel.batch_inference_endpoint || '',
		},
	});

	const handleSubmitClick = async (data: ICreateAIModelRequest) => {
		if (!siteSlug) return;

		dispatch(
			updateAIModel({
				id: aiModel.id,
				request: data,
				siteSlug: siteSlug,
				onSuccess: (updatedModel) => {
					snackbar('AI model updated successfully', 'success');
					reset({
						name: updatedModel.name,
						description: updatedModel.description || '',
						active: updatedModel.active,
						inference_endpoint: updatedModel.inference_endpoint,
						liveness_endpoint: updatedModel.liveness_endpoint || '',
						batch_inference_endpoint:
							updatedModel.batch_inference_endpoint || '',
					});
				},
			}),
		);
	};

	const handleReset = () => {
		reset({
			name: aiModel.name,
			description: aiModel.description || '',
			active: aiModel.active,
			inference_endpoint: aiModel.inference_endpoint,
			liveness_endpoint: aiModel.liveness_endpoint || '',
			batch_inference_endpoint: aiModel.batch_inference_endpoint || '',
		});
	};

	useEffect(() => {
		handleReset();
	}, [aiModel, reset]);

	return (
		<Box
			sx={{
				backgroundColor: 'white',
				padding: 2,
				borderRadius: '4px',
			}}
		>
			<Typography
				variant='h6'
				sx={{
					fontSize: '20px',
					fontWeight: 500,
					color: 'rgba(0, 0, 0, 0.87)',
					mb: 3,
					textAlign: 'left',
				}}
			>
				AI Model Details
			</Typography>

			<form onSubmit={handleSubmit(handleSubmitClick)}>
				<Grid container spacing={3}>
					{/* Row 1 */}
					<Grid item xs={12} md={6}>
						<Controller
							name='name'
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									label='Name'
									variant='outlined'
									fullWidth
									error={!!errors.name}
									helperText={errors.name?.message}
									inputProps={{ maxLength: 255 }}
									disabled={isLoading}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<Controller
							name='description'
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									label='Description'
									variant='outlined'
									fullWidth
									error={!!errors.description}
									helperText={errors.description?.message}
									inputProps={{ maxLength: 255 }}
									disabled={isLoading}
								/>
							)}
						/>
					</Grid>

					{/* Row 2 */}
					<Grid item xs={12} md={6}>
						<Controller
							name='inference_endpoint'
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									label='Inference Endpoint'
									variant='outlined'
									fullWidth
									error={!!errors.inference_endpoint}
									helperText={
										errors.inference_endpoint?.message
									}
									disabled={isLoading}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<Controller
							name='liveness_endpoint'
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									label='Liveness Endpoint'
									variant='outlined'
									fullWidth
									error={!!errors.liveness_endpoint}
									helperText={
										errors.liveness_endpoint?.message
									}
									disabled={isLoading}
								/>
							)}
						/>
					</Grid>

					{/* Row 3 */}
					<Grid item xs={12} md={6}>
						<Controller
							name='batch_inference_endpoint'
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									label='Batch Inference Endpoint'
									variant='outlined'
									fullWidth
									error={!!errors.batch_inference_endpoint}
									helperText={
										errors.batch_inference_endpoint?.message
									}
									disabled={isLoading}
								/>
							)}
						/>
					</Grid>
					<Grid
						item
						xs={12}
						md={6}
						sx={{ display: 'flex', alignItems: 'center' }}
					>
						<Controller
							name='active'
							control={control}
							render={({ field }) => (
								<FormControlLabel
									control={
										<Switch
											checked={field.value}
											onChange={(e) =>
												field.onChange(e.target.checked)
											}
											disabled={isLoading}
										/>
									}
									label='Active'
								/>
							)}
						/>
					</Grid>

					{/* Action Buttons */}
					<Grid item xs={12}>
						<Box
							sx={{
								display: 'flex',
								gap: 2,
								justifyContent: 'flex-start',
								mt: 1,
							}}
						>
							<Box sx={{ width: '150px' }}>
								<PrimaryButton
									type='submit'
									variant='contained'
									fullWidth
									disabled={!isDirty || isLoading}
									isLoading={isLoading}
									sx={{ height: '36px' }}
								>
									UPDATE
								</PrimaryButton>
							</Box>
							<Box sx={{ width: '150px' }}>
								<Button
									variant='outlined'
									color='error'
									fullWidth
									disabled={!isDirty || isLoading}
									onClick={handleReset}
									sx={{ height: '36px' }}
								>
									RESET
								</Button>
							</Box>
						</Box>
					</Grid>
				</Grid>
			</form>
		</Box>
	);
};

export default AIModelDetailForm;
